// Central react-pdf wrapper. Points PDF.js at a CDN worker so no
// bundler config is required, and wraps the raw <Page> component with
// a fixed-width canvas that scales to its container. Every consumer
// (designer + signer) uses the same PdfCanvas so the coordinate space
// is identical across the two flows.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function usePdfDocument(url) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const onDocLoad = useCallback((d) => {
    setDoc({ numPages: d.numPages });
    setErr(null);
  }, []);
  const onDocErr = useCallback((e) => setErr(e), []);
  return { doc, err, onDocLoad, onDocErr };
}

/**
 * Fixed-width PDF page renderer. Reports back the rendered pixel size
 * via `onSize({width,height})` so overlays can position themselves in
 * normalised 0..1 coordinates. Children render inside a positioned
 * layer that sits exactly on top of the PDF canvas.
 */
export function PdfCanvas({ file, pageIndex, width, children, onSize, onLoadSuccess, onLoadError, className }) {
  const [pageSize, setPageSize] = useState(null);
  const wrapRef = useRef(null);
  return (
    <Document
      file={file}
      onLoadSuccess={onLoadSuccess}
      onLoadError={onLoadError}
      loading={<div style={{ padding: 24, color: "#5C738A" }}>Loading PDF…</div>}
      error={<div style={{ padding: 24, color: "#b23" }}>Could not load PDF.</div>}
    >
      <div ref={wrapRef} className={className} style={{ position: "relative", display: "inline-block", background: "#fff", boxShadow: "0 4px 16px rgba(1,35,64,0.12)" }}>
        <Page
          pageNumber={pageIndex + 1}
          width={width}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={(page) => {
            const size = { width: page.width, height: page.height };
            setPageSize(size);
            onSize?.(size);
          }}
        />
        {pageSize && (
          <div
            style={{
              position: "absolute", inset: 0,
              width: pageSize.width, height: pageSize.height,
              pointerEvents: "none",
            }}
          >
            {typeof children === "function" ? children(pageSize) : children}
          </div>
        )}
      </div>
    </Document>
  );
}

/** Convert normalised (0..1) rect → pixel rect at current page size. */
export function toPixel(rect, pageSize) {
  return {
    left: rect.x * pageSize.width,
    top: rect.y * pageSize.height,
    width: rect.width * pageSize.width,
    height: rect.height * pageSize.height,
  };
}

/** Convert pixel rect at page size → normalised (0..1). */
export function toNorm(rect, pageSize) {
  return {
    x: rect.left / pageSize.width,
    y: rect.top / pageSize.height,
    width: rect.width / pageSize.width,
    height: rect.height / pageSize.height,
  };
}
